export function getEffectiveAvailability(car) {
  if (!car) return "AVAILABLE";
  
  const rawAvail = car.availabilityStatus || "AVAILABLE";
  
  if (rawAvail === "AVAILABLE") {
    return "AVAILABLE";
  }

  const changedAt = car.availabilityStatusChangedAt;
  if (!changedAt) {
    // If no timestamp is present but status is not AVAILABLE, 
    // it's likely a legacy record. We could either honor it or reset it.
    // Given the strict 24h requirement, it's safer to honor it if we don't know when it was set, 
    // OR we can assume it's old and reset it. Let's assume it's old and reset it to be safe, 
    // but typically we should honor it if no timestamp. I'll honor it if no timestamp.
    return rawAvail;
  }

  // Calculate hours passed
  const hoursPassed = (Date.now() - new Date(changedAt).getTime()) / (1000 * 60 * 60);
  
  if (hoursPassed >= 24) {
    return "AVAILABLE";
  }
  
  return rawAvail;
}

/* ── The weekly schedule ──────────────────────────────────────────────────────
   Every onboarding asks for working hours - Mon-Sun, a start and an end, and an
   on/off per day - and nothing used them: the list treated a car as available unless
   its owner had flipped a manual On Trip / Maintenance switch, which nobody has. The
   schedule is read in India time, because that is where every partner works and it
   must not change with a customer's phone set to another zone. */
const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function istDayAndMinute(when) {
  const d = when instanceof Date ? when : new Date(when);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const day = get("weekday");
  const minute = Number(get("hour")) * 60 + Number(get("minute"));
  return DAY_KEYS.includes(day) && Number.isFinite(minute) ? { day, minute } : null;
}

const toMinute = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Whether a weekly schedule has the partner working at a given moment.
 *
 * Returns null when it cannot say - no schedule, an unreadable time, a day missing
 * from the map - so the caller treats the car as available rather than hiding a
 * partner because of a gap in our own data. An "end" of 23:59 covers the whole last
 * minute, and a range that crosses midnight (22:00-06:00) is honoured on both sides.
 */
export function isWithinSchedule(schedule, when = new Date()) {
  if (!schedule || typeof schedule !== "object") return null;
  const at = istDayAndMinute(when);
  if (!at) return null;
  const day = schedule[at.day];
  if (!day || typeof day !== "object") return null;
  if (day.enabled === false) return false;
  if (day.status && String(day.status).toLowerCase() !== "available") return false;

  const start = toMinute(day.start), end = toMinute(day.end);
  if (start == null || end == null) return null;
  if (start <= end) return at.minute >= start && at.minute <= end;
  return at.minute >= start || at.minute <= end;
}

/**
 * Availability at the moment the customer wants the car.
 *
 *   ON_TRIP / MAINTENANCE  the owner's own switch, which already expires after 24h
 *   OUTSIDE_HOURS          switched on, but not working at that time
 *   AVAILABLE              otherwise, including whenever the schedule cannot say
 *
 * `when` is the trip's pickup time when one is set, otherwise now.
 */
export function getAvailabilityAt(car, when = new Date()) {
  const manual = getEffectiveAvailability(car);
  if (manual !== "AVAILABLE") return manual;
  return isWithinSchedule(car?.weeklySchedule, when) === false ? "OUTSIDE_HOURS" : "AVAILABLE";
}
